import { Fragment, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Menu, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { FaEdit, FaCog, FaTrash, FaEllipsisV } from "react-icons/fa";
import { FiCheck, FiCode, FiCopy, FiLock, FiUnlock, FiPlus, FiGitBranch, FiExternalLink, FiEdit2 } from "react-icons/fi";
import { IoShareSocial } from "react-icons/io5";
import toast from "react-hot-toast";

import api from "@/services/api";
import Loader from "@/components/loader";
import { CANDIDATE_URL } from "@/config";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    tab_edit: "Information",
    tab_pipeline: "Pipeline",
    tab_settings: "Application Form",
    tab_distribution: "Distribution",
    tab_raw_data: "Raw Data",
    view_job: "View Job",
    close_job: "Close Job",
    reopen_job: "Reopen Job",
    open_job: "Open Job",
    back_to_draft: "Back to Draft",
    back_to_draft_success: "Job moved back to draft",
    delete_job: "Delete Job",
    duplicate_job: "Duplicate Job",
    import_candidate: "Import Candidate",
    open_success: "Job opened successfully!",
    close_success: "Job closed successfully!",
    delete_success: "Job deleted successfully!",
    duplicate_success: "Job duplicated successfully!",
    delete_confirm: "Are you sure you want to delete this job? This action cannot be undone.",
    missing_fields: "Fill in required fields before publishing",
  },
  fr: {
    tab_edit: "Informations",
    tab_pipeline: "Pipeline",
    tab_settings: "Formulaire",
    tab_distribution: "Distribution",
    tab_raw_data: "Données Brutes",
    view_job: "Voir le poste",
    close_job: "Fermer le poste",
    reopen_job: "Rouvrir le poste",
    open_job: "Ouvrir le poste",
    back_to_draft: "Remettre en brouillon",
    back_to_draft_success: "Poste remis en brouillon",
    delete_job: "Supprimer le poste",
    duplicate_job: "Dupliquer le poste",
    import_candidate: "Importer un candidat",
    open_success: "Poste ouvert avec succès !",
    close_success: "Poste fermé avec succès !",
    delete_success: "Poste supprimé avec succès !",
    duplicate_success: "Poste dupliqué avec succès !",
    delete_confirm: "Êtes-vous sûr de vouloir supprimer ce poste ? Cette action est irréversible.",
    missing_fields: "Remplis les champs requis avant de publier",
  },
};

function isReadyToPublish(job) {
  return job?.title && job?.description?.length >= 1 && job?.contract && job?.employment_type && job?.workplace_type;
}

export default function ActionsMenu({ job, setJob, posts = [], setShowImportModal }) {
  const { t } = useComponentTranslation(translations);
  const { id } = useParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const isDraft = job?.status === "DRAFT";
  const canPublish = isReadyToPublish(job);

  const handleStatusChange = async (newStatus, message) => {
    setBusy(true);
    try {
      const payload = {
        ...job,
        status: newStatus,
        publication_date: newStatus === "OPEN" ? (job.publication_date || new Date()) : job.publication_date,
      };
      if (newStatus === "DRAFT") payload.publication_date = null;
      if (newStatus === "OPEN" && !job.publication_date) payload.publication_date = new Date();

      const response = await api.put(`/job/${id}`, payload);
      if (!response.ok) return toast.error(response.data.message);

      toast.success(message);
      setJob(response.data);
      if (newStatus === "OPEN") navigate(`/recruiter/job-ads/${id}/candidates`);
    } catch (e) {
      console.error(e);
      toast.error(e.code);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("delete_confirm"))) return;
    setBusy(true);
    try {
      const response = await api.remove(`/job/${id}`);
      if (!response.ok) return toast.error(response.data.message);
      toast.success(t("delete_success"));
      navigate("/recruiter/job-ads");
    } catch (e) {
      console.error(e);
      toast.error(e.code);
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async () => {
    setBusy(true);
    try {
      const response = await api.post(`/job/${id}/duplicate`);
      if (!response.ok) return toast.error(response.data.message || "Failed to duplicate job");
      toast.success(t("duplicate_success"));
      navigate(`/recruiter/job-ads/${response.data._id}`);
    } catch (e) {
      console.error(e);
      toast.error(e.code || "Failed to duplicate job");
    } finally {
      setBusy(false);
    }
  };

  const jobegoPost = posts.find((p) => p.channel_name?.toLowerCase() === "jobego");

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={busy}
      >
        <FaEllipsisV className="h-5 w-5" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-lg shadow-lg z-50 border border-gray-200 focus:outline-none">
          <div className="py-1">
            {isDraft && (
              <>
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                        canPublish ? "text-emerald-600" : "text-gray-400 cursor-not-allowed"
                      }`}
                      onClick={() => canPublish && handleStatusChange("OPEN", t("open_success"))}
                      disabled={busy || !canPublish}
                      title={canPublish ? "" : t("missing_fields")}
                    >
                      {busy ? <Loader size="small" color="gray" /> : <FiUnlock className="h-4 w-4" />}
                      <span>{t("open_job")}</span>
                    </button>
                  )}
                </MenuItem>
                <div className="border-t border-gray-200" />
              </>
            )}

            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  onClick={() => setShowImportModal(true)}
                >
                  <FiPlus className="h-4 w-4" />
                  <span>{t("import_candidate")}</span>
                </button>
              )}
            </MenuItem>
            {jobegoPost && (
              <MenuItem>
                {({ active }) => (
                  <a
                    href={`${CANDIDATE_URL}/companies/${job.organization_slug}/jobs/${jobegoPost._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  >
                    <FiExternalLink className="h-4 w-4" />
                    <span>{t("view_job")}</span>
                  </a>
                )}
              </MenuItem>
            )}
            <div className="border-t border-gray-200" />

            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  onClick={() => navigate(`/recruiter/job-ads/${id}/ad/job`)}
                >
                  <FaEdit className="h-4 w-4" />
                  <span>{t("tab_edit")}</span>
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  onClick={() => navigate(`/recruiter/job-ads/${id}/ad/application`)}
                >
                  <FaCog className="h-4 w-4" />
                  <span>{t("tab_settings")}</span>
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  onClick={() => navigate(`/recruiter/job-ads/${id}/pipeline`)}
                >
                  <FiGitBranch className="h-4 w-4" />
                  <span>{t("tab_pipeline")}</span>
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  onClick={() => navigate(`/recruiter/job-ads/${id}/ad/distribution`)}
                >
                  <IoShareSocial className="h-4 w-4" />
                  <span>{t("tab_distribution")}</span>
                </button>
              )}
            </MenuItem>
            <div className="border-t border-gray-200" />

            {job?.status === "OPEN" && (
              <>
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                      onClick={() => handleStatusChange("CLOSED", t("close_success"))}
                      disabled={busy}
                    >
                      {busy ? <Loader size="small" color="gray" /> : <FiLock className="h-4 w-4" />}
                      <span>{t("close_job")}</span>
                    </button>
                  )}
                </MenuItem>
                <div className="border-t border-gray-200" />
              </>
            )}
            {job?.status === "CLOSED" && (
              <>
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                      onClick={() => handleStatusChange("OPEN", t("open_success"))}
                      disabled={busy}
                    >
                      {busy ? <Loader size="small" color="gray" /> : <FiUnlock className="h-4 w-4" />}
                      <span>{t("reopen_job")}</span>
                    </button>
                  )}
                </MenuItem>
                <div className="border-t border-gray-200" />
              </>
            )}

            {!isDraft && (
              <MenuItem>
                {({ active }) => (
                  <button
                    className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                    onClick={() => handleStatusChange("DRAFT", t("back_to_draft_success"))}
                    disabled={busy}
                  >
                    {busy ? <Loader size="small" color="gray" /> : <FiEdit2 className="h-4 w-4" />}
                    <span>{t("back_to_draft")}</span>
                  </button>
                )}
              </MenuItem>
            )}
            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  onClick={handleDuplicate}
                  disabled={busy}
                >
                  {busy ? <Loader size="small" color="gray" /> : <FiCopy className="h-4 w-4" />}
                  <span>{t("duplicate_job")}</span>
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-red-600 flex items-center gap-2`}
                  onClick={handleDelete}
                  disabled={busy}
                >
                  {busy ? <Loader size="small" color="red" /> : <FaTrash className="h-4 w-4" />}
                  <span>{t("delete_job")}</span>
                </button>
              )}
            </MenuItem>
            <div className="border-t border-gray-200" />
            <MenuItem>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100" : ""} w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                  onClick={() => navigate(`/recruiter/job-ads/${id}/raw-data`)}
                >
                  <FiCode className="h-4 w-4" />
                  <span>{t("tab_raw_data")}</span>
                </button>
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
}
